/**
 * CorporatePeriodSelector
 *
 * Fetches /api/corporate/periods and lets the user pick a reporting period.
 * Defaults to the active period. Used by all three corporate dashboards
 * (LCA, GRI, BRSR).
 *
 * Also creates periods. Until this existed there was no way to create one
 * anywhere in the app — every newly provisioned tenant landed on "No reporting
 * periods configured yet. Create one to enable corporate rollup." with nothing
 * to click, and the corporate rollup plus "New Project" stayed unusable until
 * someone POSTed to /api/corporate/periods by hand.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../../context/PermissionContext';

const API = process.env.REACT_APP_BACKEND_URL;

// POST /api/corporate/periods requires ANY of brsr/gri/lca write_corporate
// (backend/routes/corporate_periods.py _has_any_corporate_write). Mirrored
// here so the button never offers an action the API will reject.
const CORPORATE_RESOURCES = ['brsr', 'gri', 'lca'];

/**
 * Mirrors backend _has_any_corporate_write: any of brsr/gri/lca
 * write_corporate.
 *
 * Deliberately takes getResourcePermissions, which filters on `resource` only.
 * usePermissions().hasPermission() also matches `module_slug`, so it cannot
 * distinguish these resources and would grant the button too widely.
 */
export const canCreatePeriods = (getResourcePermissions) =>
  CORPORATE_RESOURCES.some((resource) =>
    (getResourcePermissions(resource) || []).some(
      (p) => p.action === 'write_corporate'
    )
  );

/** Sensible default window: the fiscal year containing today, Apr–Mar. */
export const defaultFiscalYear = (today = new Date()) => {
  const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    name: `FY${startYear + 1}`,
    start_date: `${startYear}-04-01`,
    end_date: `${startYear + 1}-03-31`,
  };
};

const CorporatePeriodSelector = ({ value, onChange }) => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultFiscalYear);

  // getResourcePermissions filters on `resource` only. hasPermission() would
  // also match module_slug, which cannot distinguish these resources — see
  // PermissionContext.hasPermission.
  const { getResourcePermissions } = usePermissions();
  const canCreate = canCreatePeriods(getResourcePermissions);

  const fetchPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/corporate/periods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.periods || [];
      setPeriods(list);
      if (!value && list.length > 0) {
        const active = list.find((p) => p.is_active) || list[0];
        if (active) onChange(active.id);
      }
    } catch (err) {
      console.error('Failed to load corporate periods', err);
    } finally {
      setLoading(false);
    }
  }, [value, onChange]);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  const openCreate = () => {
    setForm(defaultFiscalYear());
    setShowCreate(true);
  };

  const createPeriod = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Give the period a name, e.g. FY2026');
      return;
    }
    // The API rejects end_date < start_date with a 400; catch it here so the
    // user sees it against the field rather than as a toast after a round trip.
    if (form.end_date < form.start_date) {
      toast.error('End date must be on or after the start date');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/corporate/periods`,
        {
          name: form.name.trim(),
          fiscal_year_label: form.name.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          // First period must be active, or the rollup stays empty and
          // "New Project" stays disabled — the dead end this UI exists to fix.
          is_active: periods.length === 0 ? true : !!form.is_active,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const created = res.data?.period;
      toast.success(`Reporting period ${created?.name || form.name} created`);
      setShowCreate(false);

      // Re-fetch so is_active flags reflect the server (creating an active
      // period deactivates the previous one), then select the new period.
      const refreshed = await axios.get(`${API}/api/corporate/periods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPeriods(refreshed.data?.periods || []);
      if (created?.id) onChange(created.id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Could not create the reporting period'
      );
    } finally {
      setSaving(false);
    }
  };

  const createButton = (label) => (
    <button
      type="button"
      onClick={openCreate}
      data-testid="create-period-btn"
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50"
      title="Create a reporting period (e.g. FY2026)"
    >
      <Plus className="w-4 h-4" /> {label}
    </button>
  );

  const dialog = showCreate && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold text-gray-900">
            New reporting period
          </h3>
          <button
            type="button"
            onClick={() => setShowCreate(false)}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={createPeriod} className="space-y-4 px-5 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="FY2026"
              data-testid="period-name-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-500">
              Studies and LCA projects are pinned to this period, so name it
              the way your reporting year is known internally.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="period-start-input"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid="period-end-input"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {periods.length === 0 ? (
            <p className="text-xs text-gray-600 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              This will be your first period, so it becomes the active one.
            </p>
          ) : (
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="period-active-input"
                className="mt-0.5"
              />
              <span>
                Make this the active period
                <span className="block text-xs text-gray-500">
                  The current active period is deactivated — only one can be
                  active at a time.
                </span>
              </span>
            </label>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="period-submit-btn"
              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40"
            >
              {saving ? 'Creating…' : 'Create period'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <Calendar className="w-4 h-4 animate-pulse" />
        Loading periods…
      </div>
    );
  }

  if (periods.length === 0) {
    return (
      <>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">
            {canCreate
              ? 'No reporting periods configured yet. Create one to enable corporate rollup.'
              : 'No reporting periods configured yet. Ask an administrator to create one.'}
          </div>
          {canCreate && createButton('New period')}
        </div>
        {dialog}
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-gray-500" />
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          {periods.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}{p.is_active ? ' (active)' : ''}
            </option>
          ))}
        </select>
        {canCreate && (
          <button
            type="button"
            onClick={openCreate}
            data-testid="create-period-btn"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            title="Create a reporting period"
            aria-label="Create a reporting period"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
      {dialog}
    </>
  );
};

export default CorporatePeriodSelector;
